// Shared optimistic-cache helpers. Mutations use these to patch every cached
// list variant of an entity (across scopes, filters, and other key suffixes)
// in one call, with snapshot/rollback support.
//
// Two cache shapes are supported:
//   - `InfiniteData<P>` from useInfiniteQuery — wraps pages[] + pageParams[].
//   - Plain `P` from useQuery — a single page-like value with an `items` field.
//
// The pattern intentionally accepts a `prefix: QueryKey` and uses TanStack's
// built-in prefix matching. The prefix MUST resolve to a key segment that the
// registered query keys actually start with — i.e. the keys factory needs a
// proper prefix function. Adding non-identity fetch params (pageSize, cursor)
// inside a key segment after the resource identity breaks prefix matching and
// is the most common cause of "optimistic patch silently does nothing" bugs.

import type {
  InfiniteData,
  QueryClient,
  QueryKey,
} from '@tanstack/react-query';

type ListShape<T> = { items: readonly T[] };

type Snapshot = readonly [readonly unknown[], unknown];

export type SnapshotSet = { previousLists: Snapshot[] };

// `getQueriesData` matches every cache entry whose key STARTS WITH the
// supplied prefix — and at a single prefix you may legitimately have a
// mix of `InfiniteData<P>` and bare `P` (single-page) shapes, depending on
// which hook registered the query. Each helper below filters to the shape
// it knows how to transform; passing one helper a foreign shape and
// dereferencing the wrong field (e.g. `value.items` on InfiniteData) is
// the exact bug that produced "Cannot read properties of undefined
// (reading 'map')" in the message-send flow when patchItemInLists fanned
// the same transform across both helpers.
function isInfinite(value: unknown): value is InfiniteData<unknown> {
  return (
    value !== null &&
    typeof value === 'object' &&
    Array.isArray((value as { pages?: unknown }).pages)
  );
}
function isListPage(value: unknown): value is ListShape<unknown> {
  return (
    value !== null &&
    typeof value === 'object' &&
    Array.isArray((value as { items?: unknown }).items) &&
    !isInfinite(value)
  );
}

/**
 * Patch every InfiniteData list cache whose key starts with `prefix` by
 * transforming each page's items. Returns snapshots for rollback.
 *
 * Use when the user-visible effect of a mutation is "this item should
 * appear/disappear/change in any list it could be a member of" — feed
 * reactions, message append, save/unsave, follow toggles affecting member
 * lists, etc.
 */
export function patchAllInfiniteLists<P extends ListShape<T>, T>(
  qc: QueryClient,
  prefix: QueryKey,
  transform: (items: readonly T[]) => readonly T[],
  predicate?: (key: readonly unknown[]) => boolean,
): SnapshotSet {
  return patchAllInfinitePages<P>(
    qc,
    prefix,
    (page) => ({ ...page, items: transform(page.items) }),
    predicate,
  );
}

/**
 * Patch every non-infinite list cache (single page shape `P extends { items }`)
 * whose key starts with `prefix`. Same semantics as `patchAllInfiniteLists`.
 */
export function patchAllLists<P extends ListShape<T>, T>(
  qc: QueryClient,
  prefix: QueryKey,
  transform: (items: readonly T[]) => readonly T[],
  predicate?: (key: readonly unknown[]) => boolean,
): SnapshotSet {
  return patchAllListPages<P>(
    qc,
    prefix,
    (page) => ({ ...page, items: transform(page.items) }),
    predicate,
  );
}

/**
 * Whole-page variant of `patchAllInfiniteLists`, for list pages that carry
 * entities outside `items` too (a page-1 side row, a pinned slot) that the
 * same mutation must reach.
 */
export function patchAllInfinitePages<P extends ListShape<unknown>>(
  qc: QueryClient,
  prefix: QueryKey,
  transform: (page: P) => P,
  predicate?: (key: readonly unknown[]) => boolean,
): SnapshotSet {
  const entries = qc.getQueriesData<unknown>({ queryKey: prefix });
  const previousLists: Snapshot[] = [];
  for (const [key, value] of entries) {
    if (!isInfinite(value)) continue;
    if (predicate !== undefined && !predicate(key)) continue;
    const infValue = value as InfiniteData<P>;
    previousLists.push([key, infValue]);
    qc.setQueryData<InfiniteData<P>>(key, {
      ...infValue,
      pages: infValue.pages.map(transform),
    });
  }
  return { previousLists };
}

/**
 * Whole-page variant of `patchAllLists`. Same semantics as
 * `patchAllInfinitePages`.
 */
export function patchAllListPages<P extends ListShape<unknown>>(
  qc: QueryClient,
  prefix: QueryKey,
  transform: (page: P) => P,
  predicate?: (key: readonly unknown[]) => boolean,
): SnapshotSet {
  const entries = qc.getQueriesData<unknown>({ queryKey: prefix });
  const previousLists: Snapshot[] = [];
  for (const [key, value] of entries) {
    if (!isListPage(value)) continue;
    if (predicate !== undefined && !predicate(key)) continue;
    const listValue = value as P;
    previousLists.push([key, listValue]);
    qc.setQueryData<P>(key, transform(listValue));
  }
  return { previousLists };
}

/**
 * Patch a single item, by id, across every cached list (infinite or not)
 * matching `prefix`. The patch fn receives the existing item and returns its
 * new shape; items not matching `id` are passed through.
 */
export function patchItemInLists<T extends { id: string }>(
  qc: QueryClient,
  prefix: QueryKey,
  id: string,
  patch: (item: T) => T,
): SnapshotSet {
  const apply = (items: readonly T[]): readonly T[] =>
    items.map((item) => (item.id === id ? patch(item) : item));
  const inf = patchAllInfiniteLists<ListShape<T>, T>(qc, prefix, apply);
  const flat = patchAllLists<ListShape<T>, T>(qc, prefix, apply);
  return { previousLists: [...inf.previousLists, ...flat.previousLists] };
}

/**
 * Remove an item by id from every cached list matching `prefix`.
 */
export function removeItemFromLists<T extends { id: string }>(
  qc: QueryClient,
  prefix: QueryKey,
  id: string,
): SnapshotSet {
  const apply = (items: readonly T[]): readonly T[] =>
    items.filter((item) => item.id !== id);
  const inf = patchAllInfiniteLists<ListShape<T>, T>(qc, prefix, apply);
  const flat = patchAllLists<ListShape<T>, T>(qc, prefix, apply);
  return { previousLists: [...inf.previousLists, ...flat.previousLists] };
}

/**
 * Prepend an item to the FIRST page of every cached infinite-list matching
 * `prefix`. Also prepends to non-infinite lists. Optional predicate restricts
 * which list variants get the prepend (e.g. only the saved-scope list).
 */
export function prependItemToLists<T extends { id: string }>(
  qc: QueryClient,
  prefix: QueryKey,
  item: T,
  predicate?: (key: readonly unknown[]) => boolean,
): SnapshotSet {
  const previousLists: Snapshot[] = [];
  const entries = qc.getQueriesData<unknown>({ queryKey: prefix });

  for (const [key, value] of entries) {
    if (predicate !== undefined && !predicate(key)) continue;

    if (isInfinite(value)) {
      const infValue = value as InfiniteData<ListShape<T>>;
      const head = infValue.pages[0];
      // Don't double-insert if the item is already in the first page.
      if (head?.items.some((x) => x.id === item.id)) continue;
      previousLists.push([key, infValue]);
      if (head === undefined) continue;
      qc.setQueryData<InfiniteData<ListShape<T>>>(key, {
        ...infValue,
        pages: [
          { ...head, items: [item, ...head.items] },
          ...infValue.pages.slice(1),
        ],
      });
      continue;
    }

    if (isListPage(value)) {
      const listValue = value as ListShape<T>;
      if (listValue.items.some((x) => x.id === item.id)) continue;
      previousLists.push([key, listValue]);
      qc.setQueryData<ListShape<T>>(key, {
        ...listValue,
        items: [item, ...listValue.items],
      });
      continue;
    }
  }

  return { previousLists };
}

/**
 * Restore cache entries snapshotted by the patch helpers. Call this from a
 * mutation's `onError` to roll back the optimistic update.
 */
export function restoreSnapshots(
  qc: QueryClient,
  snapshots: SnapshotSet | undefined,
): void {
  if (snapshots === undefined) return;
  for (const [key, value] of snapshots.previousLists) {
    qc.setQueryData(key, value);
  }
}

/**
 * Invalidate every matching query AND force a refetch — including inactive
 * queries that no observer is currently watching.
 *
 * Why this exists (and why default `invalidateQueries` isn't enough):
 *
 * `invalidateQueries` with the default `refetchType: 'active'` only refetches
 * queries that have at least one observer. Inactive caches (e.g. the
 * `?scope=saved` list when the user is on `?scope=all`) get marked stale but
 * are NOT refetched. The next time the user navigates to that filter, the
 * UI renders the stale cache first, *then* refetch returns canonical data
 * and replaces it. That brief render of pre-mutation rows is the
 * "stale-flash" bug.
 *
 * Use this helper whenever a mutation changes which entities belong in a
 * filter view — save/unsave (saved filter), join/leave (mine filter),
 * mark-read (unread filter), accept/decline (proposals vs active), block
 * (blocked list), etc. Optimistic patches still belong in `onMutate`; this
 * is the canonical-state belt-and-suspenders for `onSettled`.
 */
export function invalidateAndRefetchAll(
  qc: QueryClient,
  filter: {
    queryKey?: QueryKey;
    predicate?: Parameters<QueryClient['invalidateQueries']>[0] extends
      | { predicate?: infer P }
      | undefined
      ? P
      : never;
  },
): void {
  void qc.invalidateQueries({ ...filter, refetchType: 'all' });
}
